import { dealHands } from "../PlayTable/bridgeCore";

/**
 * Weekly Tournament — pure logic: which week we're in, the week's boards (BOARD_COUNT) for a given
 * week seed, and Standard Butler IMP scoring. No React, no Firestore here.
 *
 * Boards are deterministic from a single master seed (revealed weekly by the
 * backend so they can't be pre-studied). Each board's deal is produced inside the
 * play table via dealHands(board.seed); here we only assign seed/dealer/vul.
 */

// 7 boards a week since 22 Sep 2026 (Paul: "Change the weekly tournament to 7 boards"). It was 10. Boards 1-7
// are the SAME deals as before (the seed is per board number), so anyone part-way through keeps their results,
// and anyone who had already played 8, 9 or 10 simply counts as finished.
export const BOARD_COUNT = 7;

// "Best hand" (Paul, 2 Oct 2026: "for future weeks it will be best hand, so you will always have the best hand at
// the table"). From this week on, the hand with the most HCP is turned to South (the player); on a tie the first of
// S, N, E, W keeps it. The four hands rotate together, so partnerships stay intact; dealer and vulnerability still
// follow the board number. Weeks before this keep their original deals. KEEP IN STEP with scripts/_weekly-lib.js
// and the backend hand-record email.
export const BEST_HAND_FROM = "2026-10-05";

// Standard duplicate dealer (by board number) and the official 16-board
// vulnerability schedule. Vul strings match the play table: "" / "NS" / "EW" / "Both".
const DEALERS = ["N", "E", "S", "W"];
const VUL_SCHEDULE = [
  "", "NS", "EW", "Both", "NS", "EW", "Both", "", "EW", "Both", "", "NS", "Both", "", "NS", "EW",
];
export const boardDealer = (boardNo) => DEALERS[(boardNo - 1) % 4];
export const boardVul = (boardNo) => VUL_SCHEDULE[(boardNo - 1) % 16];

// ── deterministic seeds ────────────────────────────────────────────────────────
/** FNV-1a hash of a string -> uint32. */
export function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
/** Mix a master seed with a board index -> a uint32 deal seed. */
function mixSeed(masterSeed, i) {
  let h = (masterSeed ^ Math.imul(i ^ (i >>> 15), 2246822519)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 3266489909) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

const HCP = { A: 4, K: 3, Q: 2, J: 1 };
const hcpOf = (cards) => cards.reduce((n, c) => n + (HCP[c.rank] || 0), 0);
const RING = ["N", "E", "S", "W"];
/** Turn the strongest hand to South (see BEST_HAND_FROM). */
export function bestHandDeal(hands) {
  let best = "S";
  for (const s of ["S", "N", "E", "W"]) if (hcpOf(hands[s]) > hcpOf(hands[best])) best = s;
  const shift = (RING.indexOf("S") - RING.indexOf(best) + 4) % 4;
  if (!shift) return hands;
  const out = {};
  RING.forEach((s, i) => { out[RING[(i + shift) % 4]] = hands[s]; });
  return out;
}

/** The week's boards: [{ boardNo, seed, dealer, vul, hands }]. hands = the four hands as played. */
export function weeklyBoards(masterSeed, count = BOARD_COUNT, weekId = null) {
  const best = !!weekId && weekId >= BEST_HAND_FROM;
  const boards = [];
  for (let i = 1; i <= count; i++) {
    const seed = mixSeed(masterSeed >>> 0, i);
    const dealt = dealHands(seed);
    boards.push({ boardNo: i, seed, dealer: boardDealer(i), vul: boardVul(i), hands: best ? bestHandDeal(dealt) : dealt, bestHand: best });
  }
  return boards;
}

// ── weekId ─────────────────────────────────────────────────────────────────────
// ── The week (tournament + weekly points) ── Paul, 28 Sep 2026: "just close the tournament now, in future advertise
// close at 7am" -> weeks run Monday 07:00 Australia/Sydney to the next (DST-aware). Before WEEK_CUTOVER they ran
// Monday 00:00 UTC; the week that began 21 Sep closed early, at WEEK_CUTOVER (9:30am Sydney, 28 Sep 2026).
// The id is the week's Monday date (Sydney). KEEP THIS BLOCK IDENTICAL in content-app/lib/thisMonth.js,
// src/components/Tournament/weeklyBoards.js and the getWeeklyTournament cloud function.
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEK_CUTOVER = Date.parse("2026-09-27T23:30:00Z");
const sydneyOffset = (ms) => {
  const p = {};
  new Intl.DateTimeFormat("en-US", { timeZone: "Australia/Sydney", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .formatToParts(new Date(ms)).forEach((x) => { p[x.type] = x.value; });
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000;
};
// { start (ms), id } of the week holding `ms`.
const weekOf = (ms = Date.now()) => {
  if (ms < WEEK_CUTOVER) {
    const SHIFT = 4 * 24 * 60 * 60 * 1000; // epoch day 0 was a Thursday
    const start = Math.floor((ms - SHIFT) / WEEK_MS) * WEEK_MS + SHIFT;
    return { start, id: new Date(start).toISOString().slice(0, 10) };
  }
  const local = ms + sydneyOffset(ms);
  const d = new Date(local);
  let startLocal = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - ((d.getUTCDay() + 6) % 7), 7);
  if (local < startLocal) startLocal -= WEEK_MS;
  let start = startLocal - sydneyOffset(ms);
  start = startLocal - sydneyOffset(start);
  return { start: Math.max(start, WEEK_CUTOVER), id: new Date(startLocal).toISOString().slice(0, 10) };
};
// When the week holding `ms` ends (= the next week's start).
const weekEnd = (ms = Date.now()) => {
  const w = weekOf(ms);
  if (w.start < WEEK_CUTOVER) return Math.min(w.start + WEEK_MS, WEEK_CUTOVER);
  return weekOf(w.start + WEEK_MS + 3 * 60 * 60 * 1000).start;
};export function currentWeekId() {
  return weekOf().id;
}

// ── Standard Butler IMP scoring ────────────────────────────────────────────────
// Upper bound of the point difference for each IMP value (index = IMPs).
const IMP_UPPER = [
  10, 40, 80, 120, 160, 210, 260, 310, 360, 420, 490, 590, 740, 890, 1090, 1290, 1490, 1740, 1990,
  2240, 2490, 2990, 3490, 3990,
];
function impFromAbs(diff) {
  for (let i = 0; i < IMP_UPPER.length; i++) if (diff <= IMP_UPPER[i]) return i;
  return 24;
}
/** Signed IMPs for a score difference (your raw NS score minus the datum). */
export function impDiff(scoreDiff) {
  const imp = impFromAbs(Math.abs(Math.round(scoreDiff)));
  return scoreDiff >= 0 ? imp : -imp;
}

/**
 * Butler leaderboard from raw entries. entries: [{ uid, displayName, boardNo, rawScoreNS }].
 * Datum per board = mean of all NS raw scores on that board (simple Butler).
 * Returns [{ uid, displayName, totalImps, boardsPlayed }] sorted best-first, plus the
 * per-(uid,board) IMPs map for detail views.
 */
export function computeLeaderboard(allEntries) {
  // Only the week's official boards count, so everyone is compared on the same deals. Results on boards
  // 8-10 from the weeks when there were ten stay in the database but are left out of the totals; those
  // players stay on the leaderboard on their first seven (Paul, 22 Sep 2026).
  const entries = allEntries.filter((e) => Number(e.boardNo) >= 1 && Number(e.boardNo) <= BOARD_COUNT);
  const byBoard = new Map();
  for (const e of entries) {
    if (!byBoard.has(e.boardNo)) byBoard.set(e.boardNo, []);
    byBoard.get(e.boardNo).push(e);
  }
  const datums = new Map();
  for (const [boardNo, list] of byBoard) {
    const mean = list.reduce((s, e) => s + (e.rawScoreNS || 0), 0) / list.length;
    datums.set(boardNo, mean);
  }
  const byUid = new Map();
  const impByUidBoard = {};
  for (const e of entries) {
    const imp = impDiff((e.rawScoreNS || 0) - datums.get(e.boardNo));
    impByUidBoard[`${e.uid}__${e.boardNo}`] = imp;
    if (!byUid.has(e.uid)) byUid.set(e.uid, { uid: e.uid, displayName: e.displayName || "Player", pairNames: e.pair && e.pair.names ? [e.pair.names.S, e.pair.names.N] : null, totalImps: 0, boardsPlayed: 0 });
    const agg = byUid.get(e.uid);
    agg.totalImps += imp;
    agg.boardsPlayed += 1;
  }
  const players = Array.from(byUid.values()).sort((a, b) => b.totalImps - a.totalImps);
  return { players, datums, impByUidBoard };
}
